import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  approveExpense,
  createExpense,
  ExpenseCreatePayload,
  ExpenseRecord,
  ExpenseStatus,
  ExpenseSummary,
  fetchReceiptBlobUrl,
  getExpenseSummary,
  listExpenses,
  parseTokenClaims,
  ReceiptUploadResult,
  rejectExpense,
  uploadReceipt,
} from "../../api";
import { Icon } from "../../icons";

const CATEGORIES = ["fuel", "toll", "maintenance", "lodging", "meals", "parking", "other"];

function todayIso(): string {
  const now = new Date();
  now.setSeconds(0, 0);
  // datetime-local input expects "YYYY-MM-DDTHH:mm" (no Z)
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

export function ExpensesPage() {
  const { t, i18n } = useTranslation(["dashboard", "common"]);
  const claims = parseTokenClaims();
  const myFirm = claims?.firm_id ?? "";
  const canReview = claims?.role === "ADMIN" || claims?.role === "OPERATOR";

  const [expenses, setExpenses] = useState<ExpenseRecord[]>([]);
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | "">("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setError(null);
    try {
      const [rows, sum] = await Promise.all([
        listExpenses({ status: statusFilter || undefined }),
        getExpenseSummary().catch(() => null),
      ]);
      setExpenses(rows);
      setSummary(sum);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function onApprove(id: string) {
    setError(null);
    setInfo(null);
    try {
      await approveExpense(id);
      setInfo(t("expenses.approvedMsg"));
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onReject(id: string) {
    setError(null);
    setInfo(null);
    const note = window.prompt(t("expenses.rejectPrompt")) ?? undefined;
    try {
      await rejectExpense(id, note || undefined);
      setInfo(t("expenses.rejectedMsg"));
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="page">
      <div className="page-head-row">
        <div>
          <h2 style={{ marginTop: 0 }}>
            <Icon name="receipt" /> {t("expenses.title")}
          </h2>
          <p className="muted">{t("expenses.intro")}</p>
        </div>
        <div>
          <label className="muted small" style={{ marginRight: "0.5rem" }}>
            {t("expenses.statusFilter")}
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ExpenseStatus | "")}
          >
            <option value="">{t("expenses.all")}</option>
            <option value="pending">{t("expenses.pendingOpt")}</option>
            <option value="approved">{t("expenses.approvedOpt")}</option>
            <option value="rejected">{t("expenses.rejectedOpt")}</option>
          </select>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}

      {summary && summary.grand_count > 0 && (
        <div className="kpi-grid" style={{ marginTop: "0.75rem" }}>
          <div className="kpi-card tone-violet">
            <div className="kpi-icon">
              <Icon name="receipt" size={22} />
            </div>
            <div className="kpi-body">
              <div className="kpi-label">{t("expenses.summaryTotal30")}</div>
              <div className="kpi-value">
                {summary.grand_total.toLocaleString(i18n.resolvedLanguage)} TRY
              </div>
              <div className="kpi-sub">{t("expenses.receiptsCount", { count: summary.grand_count })}</div>
            </div>
          </div>
          <div className="kpi-card tone-amber">
            <div className="kpi-icon">
              <Icon name="clock" size={22} />
            </div>
            <div className="kpi-body">
              <div className="kpi-label">{t("expenses.summaryPending")}</div>
              <div className="kpi-value">
                {(summary.totals_by_status.pending ?? 0).toLocaleString(i18n.resolvedLanguage)} TRY
              </div>
              <div className="kpi-sub">
                {t("expenses.receiptsCount", { count: summary.counts_by_status.pending ?? 0 })}
              </div>
            </div>
          </div>
          <div className="kpi-card tone-emerald">
            <div className="kpi-icon">
              <Icon name="check" size={22} />
            </div>
            <div className="kpi-body">
              <div className="kpi-label">{t("expenses.summaryApproved")}</div>
              <div className="kpi-value">
                {(summary.totals_by_status.approved ?? 0).toLocaleString(i18n.resolvedLanguage)} TRY
              </div>
              <div className="kpi-sub">
                {t("expenses.receiptsCount", { count: summary.counts_by_status.approved ?? 0 })}
              </div>
            </div>
          </div>
        </div>
      )}

      <h3 style={{ marginTop: "1.5rem" }}>{t("expenses.newExpense")}</h3>
      <NewExpenseForm
        firmId={myFirm}
        onCreated={() => {
          setInfo(t("expenses.savedMsg"));
          void refresh();
        }}
        onError={(e) => setError(e)}
      />

      <h3 style={{ marginTop: "1.5rem" }}>{t("expenses.listTitle")}</h3>
      {loading ? (
        <p className="muted">{t("common:actions.loading")}</p>
      ) : expenses.length === 0 ? (
        <div className="empty-card">
          <Icon name="receipt" /> {t("expenses.emptyFilter")}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t("expenses.th.date")}</th>
              <th>{t("expenses.th.category")}</th>
              <th>{t("expenses.th.amount")}</th>
              <th>{t("expenses.th.vehicle")}</th>
              <th>{t("expenses.th.vendor")}</th>
              <th>{t("expenses.th.receipt")}</th>
              <th>{t("expenses.th.status")}</th>
              <th>{t("expenses.th.createdBy")}</th>
              {canReview && <th>{t("expenses.th.action")}</th>}
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id}>
                <td>{new Date(e.occurred_at).toLocaleDateString(i18n.resolvedLanguage)}</td>
                <td>{t(`expenses.category.${e.category}`, { defaultValue: e.category })}</td>
                <td>
                  {e.amount.toLocaleString(i18n.resolvedLanguage)} {e.currency}
                </td>
                <td>{e.vehicle_id ?? "—"}</td>
                <td>{e.vendor ?? "—"}</td>
                <td>
                  {e.receipt_object_key ? (
                    <ReceiptLink expenseId={e.id} />
                  ) : (
                    <span className="muted small">—</span>
                  )}
                </td>
                <td>
                  <span className={`status-pill status-${e.status}-pill`}>
                    {t(`expenses.status.${e.status}`)}
                  </span>
                </td>
                <td>
                  <code className="small">{e.created_by}</code>
                </td>
                {canReview && (
                  <td>
                    {e.status === "pending" ? (
                      <div className="row-form">
                        <button type="button" onClick={() => onApprove(e.id)}>
                          {t("expenses.approve")}
                        </button>
                        <button
                          type="button"
                          className="danger"
                          onClick={() => onReject(e.id)}
                        >
                          {t("expenses.reject")}
                        </button>
                      </div>
                    ) : (
                      <span className="muted small">
                        {e.reviewed_by ?? "—"}
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ReceiptLink({ expenseId }: { expenseId: string }) {
  const { t } = useTranslation("dashboard");
  const [busy, setBusy] = useState(false);
  async function onClick(e: React.MouseEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      // Stream bytes through the gateway (JWT-authenticated) and open as a
      // blob URL. Blob URLs are local to the browser, no auth surface.
      const blobUrl = await fetchReceiptBlobUrl(expenseId);
      window.open(blobUrl, "_blank", "noopener");
      // The browser will release the URL when the new tab closes; we revoke
      // ourselves after a delay so a refresh in that tab still works.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <button type="button" className="link-button" onClick={onClick} disabled={busy}>
      <Icon name="receipt" size={14} /> {busy ? "..." : t("expenses.receiptOpen")}
    </button>
  );
}

function NewExpenseForm({
  firmId,
  onCreated,
  onError,
}: {
  firmId: string;
  onCreated: () => void;
  onError: (msg: string) => void;
}) {
  const { t } = useTranslation(["dashboard", "common"]);
  const [form, setForm] = useState<ExpenseCreatePayload>({
    firm_id: firmId,
    category: "fuel",
    amount: 0,
    currency: "TRY",
    occurred_at: todayIso(),
  });
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<{ file: File; preview: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<ReceiptUploadResult | null>(null);
  const [ocrApplied, setOcrApplied] = useState(false);

  function update<K extends keyof ExpenseCreatePayload>(
    key: K,
    value: ExpenseCreatePayload[K],
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      setReceipt(null);
      setUploadResult(null);
      return;
    }
    const isImage = file.type.startsWith("image/");
    const preview = isImage ? URL.createObjectURL(file) : "";
    setReceipt({ file, preview });
    setUploadResult(null);
    setOcrApplied(false);

    // Eagerly upload + OCR. The server returns parsed suggestions; we apply
    // them only if the field is still empty/default so the operator's
    // manual edits aren't clobbered.
    setUploading(true);
    try {
      const uploaded = await uploadReceipt(file);
      setUploadResult(uploaded);
      const sug = uploaded.ocr;
      if (sug) {
        setForm((prev) => {
          const next = { ...prev };
          if (sug.total_amount && (!prev.amount || prev.amount <= 0)) {
            next.amount = sug.total_amount;
          }
          if (sug.currency && prev.currency === "TRY") {
            const cur = sug.currency.toUpperCase();
            if (cur === "TL" || cur === "₺") next.currency = "TRY";
            else if (["TRY", "USD", "EUR"].includes(cur)) next.currency = cur;
          }
          if (sug.vat_amount && prev.vat_amount == null) next.vat_amount = sug.vat_amount;
          if (sug.vendor && !prev.vendor) next.vendor = sug.vendor;
          if (sug.date_iso) {
            // ISO -> datetime-local (we keep occurred_at as YYYY-MM-DDTHH:mm)
            const parsed = new Date(sug.date_iso);
            if (!Number.isNaN(parsed.getTime())) {
              const pad = (n: number) => n.toString().padStart(2, "0");
              next.occurred_at = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(
                parsed.getHours(),
              )}:${pad(parsed.getMinutes())}`;
            }
          }
          if (sug.category_hint && prev.category === "fuel") {
            // Only override the default; never clobber a deliberate choice.
            next.category = sug.category_hint;
          }
          return next;
        });
        setOcrApplied(true);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  function clearReceipt() {
    if (receipt?.preview) URL.revokeObjectURL(receipt.preview);
    setReceipt(null);
    setUploadResult(null);
    setOcrApplied(false);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.firm_id) {
      onError(t("expenses.errors.noFirm"));
      return;
    }
    if (!form.amount || form.amount <= 0) {
      onError(t("expenses.errors.amountPositive"));
      return;
    }
    setSubmitting(true);
    try {
      // If a receipt was selected and the eager upload completed, we already
      // have the key + OCR metadata in `uploadResult`. If the user typed but
      // never picked a file there's nothing to attach.
      const receiptKey = uploadResult?.object_key ?? null;
      const ocrText = uploadResult?.ocr_text ?? null;
      const ocrMd = uploadResult?.ocr ? (uploadResult.ocr as unknown as Record<string, unknown>) : null;
      const occurred = new Date(form.occurred_at).toISOString();
      await createExpense({
        ...form,
        occurred_at: occurred,
        receipt_object_key: receiptKey,
        ocr_text: ocrText,
        ocr_metadata: ocrMd,
      });
      setForm({
        firm_id: firmId,
        category: "fuel",
        amount: 0,
        currency: "TRY",
        occurred_at: todayIso(),
      });
      clearReceipt();
      onCreated();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="row-form grid" onSubmit={onSubmit}>
      <label>
        {t("expenses.form.category")}
        <select
          value={form.category}
          onChange={(e) => update("category", e.target.value)}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(`expenses.category.${c}`)}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t("expenses.form.amount")}
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.amount || ""}
          onChange={(e) => update("amount", parseFloat(e.target.value) || 0)}
          required
        />
      </label>
      <label>
        {t("expenses.form.currency")}
        <select
          value={form.currency}
          onChange={(e) => update("currency", e.target.value)}
        >
          <option value="TRY">TRY</option>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
        </select>
      </label>
      <label>
        {t("expenses.form.vat")}
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.vat_amount ?? ""}
          onChange={(e) =>
            update(
              "vat_amount",
              e.target.value === "" ? null : parseFloat(e.target.value),
            )
          }
        />
      </label>
      <label>
        {t("expenses.form.date")}
        <input
          type="datetime-local"
          value={form.occurred_at}
          onChange={(e) => update("occurred_at", e.target.value)}
          required
        />
      </label>
      <label>
        {t("expenses.form.vehicle")}
        <input
          type="text"
          value={form.vehicle_id ?? ""}
          onChange={(e) =>
            update("vehicle_id", e.target.value || null)
          }
          placeholder={t("expenses.form.vehiclePlaceholder")}
        />
      </label>
      <label>
        {t("expenses.form.vendor")}
        <input
          type="text"
          value={form.vendor ?? ""}
          onChange={(e) => update("vendor", e.target.value || null)}
          placeholder={t("expenses.form.vendorPlaceholder")}
        />
      </label>
      <label style={{ gridColumn: "1 / -1" }}>
        {t("expenses.form.note")}
        <input
          type="text"
          value={form.notes ?? ""}
          onChange={(e) => update("notes", e.target.value || null)}
          placeholder={t("expenses.form.notePlaceholder")}
        />
      </label>
      <div className="receipt-uploader" style={{ gridColumn: "1 / -1" }}>
        <label className="receipt-uploader-label">
          {t("expenses.form.receiptPhoto")}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
            onChange={onFileChange}
          />
        </label>
        {receipt && (
          <div className="receipt-preview">
            {receipt.preview ? (
              <img src={receipt.preview} alt={t("expenses.form.receiptPreviewAlt")} />
            ) : (
              <div className="receipt-pdf">
                <Icon name="receipt" /> {receipt.file.name}
              </div>
            )}
            <div className="receipt-preview-meta">
              {uploading && (
                <span className="muted small">
                  <Icon name="scan" size={14} /> {t("expenses.form.uploadingOcr")}
                </span>
              )}
              {!uploading && ocrApplied && uploadResult?.ocr && (
                <span className="info small">
                  <Icon name="check" size={14} /> {t("expenses.form.ocrApplied")}
                </span>
              )}
              {!uploading &&
                uploadResult &&
                !uploadResult.ocr &&
                uploadResult.content_type.startsWith("image/") && (
                  <span className="muted small">
                    <Icon name="alert" size={14} /> {t("expenses.form.ocrNoResult")}
                  </span>
                )}
              <button type="button" className="ghost" onClick={clearReceipt}>
                {t("expenses.form.remove")}
              </button>
            </div>
          </div>
        )}
      </div>
      <button type="submit" disabled={submitting}>
        {uploading
          ? t("expenses.form.uploadingBtn")
          : submitting
            ? t("common:actions.saving")
            : t("expenses.form.submit")}
      </button>
    </form>
  );
}
